import { AnimatePresence, motion } from "framer-motion";
import { type ReactNode, useEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import { twMerge } from "tailwind-merge";

interface GenericModalShellProps {
    children: ReactNode;
    id?: string;
    isOpen: boolean;
    onBackdropClick: () => void;
    showOverflow?: boolean;
    modalMarginSmall?: boolean;
    className?: string;
    // Focused element on open, accessibility required
    initialFocusRef: React.RefObject<HTMLElement>;
}

const GenericModalShell = ({
    children,
    id,
    isOpen,
    onBackdropClick,
    showOverflow,
    modalMarginSmall,
    initialFocusRef,
    className,
}: GenericModalShellProps) => {
    const modalRef = useRef<HTMLDivElement>(null);
    const previousFocusRef = useRef<HTMLElement | null>(null);
    const [wasOpen, setWasOpen] = useState(false);

    useEffect(() => {
        if (isOpen && !wasOpen) {
            setWasOpen(true);
            previousFocusRef.current = document.activeElement as HTMLElement;
        } else if (!isOpen && wasOpen) {
            setWasOpen(false);
        }
    }, [isOpen, wasOpen]);

    useEffect(() => {
        if (!isOpen) return;

        const focusTimer = setTimeout(() => {
            if (initialFocusRef?.current) {
                initialFocusRef.current.focus();
            } else if (modalRef.current) {
                modalRef.current.focus();
            }
        }, 150);

        return () => clearTimeout(focusTimer);
    }, [isOpen, initialFocusRef]);

    useEffect(() => {
        if (isOpen || !previousFocusRef.current) return;

        const restoreFocusTimer = setTimeout(() => {
            try {
                if (!previousFocusRef.current || !document.body.contains(previousFocusRef.current)) return;

                previousFocusRef.current.focus();
            } catch (error) {
                console.error("Error returning focus:", error);
            }
        }, 200);

        return () => clearTimeout(restoreFocusTimer);
    }, [isOpen]);

    const getFocusableElements = (): HTMLElement[] => {
        if (!modalRef.current) return [];

        const focusableSelectors = [
            "a[href]",
            "button:not([disabled])",
            "textarea:not([type='hidden']):not([disabled])",
            "input:not([type='hidden']):not([disabled])",
            "select:not([disabled])",
            '[tabindex]:not([tabindex="-1"])',
        ].join(",");

        return Array.from(
            modalRef.current.querySelectorAll(focusableSelectors)
        ) as HTMLElement[];
    };

    const navigateToElement = (targetElement: HTMLElement) => {
        targetElement.focus();
    };

    const navigateBackward = () => {
        const focusableElements = getFocusableElements();
        if (focusableElements.length === 0) return;

        const firstElement = focusableElements[0];
        const lastElement = focusableElements[focusableElements.length - 1];
        const currentIndex = focusableElements.indexOf(document.activeElement as HTMLElement);

        if (document.activeElement === firstElement || currentIndex === -1) {
            return navigateToElement(lastElement);
        }

        const prevIndex = Math.max(0, currentIndex - 1);
        navigateToElement(focusableElements[prevIndex]);
    };

    const navigateForward = () => {
        const focusableElements = getFocusableElements();
        if (focusableElements.length === 0) return;

        const firstElement = focusableElements[0];
        const lastElement = focusableElements[focusableElements.length - 1];
        const currentIndex = focusableElements.indexOf(document.activeElement as HTMLElement);

        if (document.activeElement === lastElement || currentIndex === -1) {
            return navigateToElement(firstElement);
        }

        const nextIndex = Math.min(focusableElements.length - 1, currentIndex + 1);
        navigateToElement(focusableElements[nextIndex]);
    };

    const handleEscape = () => {
        if (!onBackdropClick) return;

        onBackdropClick();
    };

    const keyHandlers: Record<string, (e: KeyboardEvent) => void> = {
        Escape: handleEscape,
        Tab: (e) => {
            e.preventDefault();
            if (e.shiftKey) {
                navigateBackward();
                return;
            }

            navigateForward();
        },
        ArrowLeft: (e) => {
            e.preventDefault();
            navigateBackward();
        },
        ArrowRight: (e) => {
            e.preventDefault();
            navigateForward();
        },
    };

    const handleKeyDown = (e: KeyboardEvent) => {
        const handler = keyHandlers[e.key];
        if (!handler) return;

        handler(e);
    };

    useEffect(() => {
        if (!isOpen || !modalRef.current) return;

        document.addEventListener("keydown", handleKeyDown);
        return () => document.removeEventListener("keydown", handleKeyDown);
    }, [isOpen, onBackdropClick]);

    return (
        <>
            {ReactDOM.createPortal(
                <AnimatePresence>
                    {isOpen && (
                        <div
                            id={id}
                            key={id}
                            className={twMerge('fixed flex items-center justify-center left-0 top-0 w-screen h-screen z-[111]', className)}
                        >
                            <motion.div
                                key="background-div"
                                onClick={() => onBackdropClick && onBackdropClick()}
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 0.3 }}
                                exit={{ opacity: 0 }}
                                className="fixed left-0 top-0 w-screen h-screen bg-black"
                            />

                            <motion.div
                                ref={modalRef}
                                key="modal-content"
                                initial={{ opacity: 0, scale: 0.75 }}
                                animate={{ opacity: 1, scale: 1 }}
                                exit={{ opacity: 0, scale: 0.75 }}
                                transition={{ duration: 0.2 }}
                                className={`relative z-10 bg-white rounded-lg p-4 shadow-xl max-w-[95vw] max-h-[95dvh] ${!showOverflow && 'overflow-y-auto overflow-x-hidden'}`}
                                tabIndex={-1} // Fallback for focus
                            >
                                <div className={`flex flex-col m-10 ${!modalMarginSmall && 'sm:m-20'}`}>
                                    {children}
                                </div>
                            </motion.div>
                        </div>
                    )}
                </AnimatePresence>,
                document.body
            )}
        </>
    );
};

export default GenericModalShell;
